import { mq, screen } from "lib/media-query";
import styled, { css } from "styled-components";
import { Screens } from "helpers/screens";
import React from "react";
import { MEDIA } from "ui/components/shared/constants";

export const DownloadAppContainer = styled.div`
    display: none;

    @media (min-width: 1024px) {
        display: block;
    }
`;

export const StyledLink = styled.a`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem;
    border-radius: 0.5rem;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.black};
    transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;

    @media (min-width: 1024px) {
        &:hover {
            background-color: ${({ theme }) => theme.colors.secondary[700]};
            color: ${({ theme }) => theme.colors.white};
            text-decoration: none;
        }
    }

    :hover {
        text-decoration: none;
    }

    &:focus,
    &:focus-visible,
    &:active {
        text-decoration: none;
        outline: none;
    }
`;

export const IconWrapper = styled.div`
    position: relative;
    height: 32px;
    width: 32px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 0.5rem;
    background-color: ${({ theme }) => theme.colors.primary[700]};

    img {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        object-fit: cover;
        transition: transform 0.3s ease-in-out;
    }

    svg {
        position: absolute;
        left: 50%;
        top: -17px;
        transform: translateX(-50%);
        transition: all 0.3s ease-in-out;
        color: ${({ theme }) => theme.colors.black};
        width: 1rem;
        height: 1rem;
    }

    @media (min-width: 1024px) {
        ${StyledLink}:hover & img {
            transform: translateY(100%);
        }

        ${StyledLink}:hover & svg {
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }
`;

export const LinkText = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.secondary[700]};
    font-weight: 700;
    transition: color 0.3s ease-in-out;

    @media (min-width: 1024px) {
        color: ${({ theme }) => theme.colors.black};
        font-weight: 400;
        ${StyledLink}:hover & {
            color: ${({ theme }) => theme.colors.white};
        }
    }
`;

export const DownloadIconTextWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.625rem;
`;

interface DividerProps {
    color?: string;
    mt?: string;
    mb?: string;
}

export const Divider = styled.hr<DividerProps>`
    border: 0;
    height: 0.0625rem;
    background-color: ${({ theme }) => theme.colors.blue};
    margin-top: ${({ mt }) => mt || "0"};
    margin-bottom: ${({ mb }) => mb || "0"};
`;

export const NavContainer = styled.nav`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.colors.primary[700]};
    height: 52px;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 3000;
    gap: 2rem;

    @media (max-width: 1320px) {
        padding: 0 1rem;
    }
`;

export const Container = styled.section`
    align-items: center;
    justify-content: space-between;
    background-color: green;
    display: flex;
    margin: 0 auto;
    max-width: ${({ theme }) => theme.appMaxWidth};
    padding: 0 7.5rem;
    ${mq.to(screen.LG)`
    justify-content: center;
    `}
`;

export const LogoWrapper = styled.div``;
export const MenusWrapper = styled.div`
    display: flex;
    margin-left: 54px;
    gap: 1.875rem;
`;
export const AccountsContainer = styled.div`
    gap: 0.75rem;
    display: flex;
    justify-content: flex-end;
`;

export const HeaderLogo = styled.a`
    cursor: pointer;
    height: 2.1rem;
    margin-right: 1.5rem;
    width: 150px;
    color: ${({ theme }) => theme.colors.secondary[700]};
    fill: ${({ theme }) => theme.colors.secondary[700]};

    :focus {
        outline: none;
    }
`;

export const HeaderLogoMobile = styled.div`
    display: flex;
    margin-left: 0.5rem;

    @media (${MEDIA.lg}) {
        display: none;
    }

    :focus {
        outline: none;
    }

    svg {
        fill: ${({ theme }) => theme.colors.secondary[700]};
    }
`;

export const Dropdown = styled.div`
    align-items: center;
    justify-content: space-between;
    display: flex;
    flex-direction: row;
    width: 100%;
    margin: 0 auto;

    @media (${MEDIA.lg}) {
        max-width: 1296px;
    }

    ${({ theme }) => mq.to(screen.LG)`
    background-color: ${theme.colors.tertiary[800]};
        position: fixed;
        width: 210px;
        transform: translateX(210px);
        height: 100vh;
        overflow-y: scroll;
        top: 0;
        right: 0;
        z-index: -10;
        padding-bottom: 7rem;
        display: block;
        margin: 0;
        &:after {
            content: "";
            box-shadow: 0 0 10px 3px rgb(0 0 0 / 50%);
            top: 0;
            position: absolute;
            bottom: -100rem;
        }
    `}
`;

export const Wrapper = styled.div`
    cursor: pointer;
    display: flex;
    gap: 1rem;

    @media (${Screens.get("lg")}) {
        right: 4px;
    }
`;

export const MenuHeader = styled.div<{ active?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.9rem;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background-color: ${({ theme, active }) =>
        active ? theme.colors.secondary[700] : theme.colors.primary[700]};
    color: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.secondary[700])};
    fill: ${({ theme }) => theme.colors.secondary[700]};
    font-weight: 600;
    font-size: 16px;

    &:hover {
        text-decoration: none;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.secondary[700]};

        svg {
            fill: ${({ theme }) => theme.colors.white};
        }
    }
`;

export const DropdownMenu = styled.ul<{ change?: boolean }>`
    cursor: default;
    top: 0;
    position: unset;
    width: 215px;
    visibility: visible;
    background-color: ${({ theme }) => theme.colors.secondary[50]};

    @media (${MEDIA.lg}) {
        position: absolute;
        top: 48px;
        padding: 0.625rem;
        min-width: 11rem;
        border-radius: 0.75rem;
        visibility: ${({ change }) => (change ? "visible" : "hidden")};
        box-shadow: 0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
`;

export const UitLoggenButton = styled.button`
    all: unset;
    display: flex;
    padding: 0.62rem 0.75rem;
    width: 100%;
    color: ${({ theme }) => theme.colors.secondary[700]};
    cursor: pointer;
    border-radius: 0.25rem;
    color: ${({ theme }) => theme.colors.black};
    font-size: 1rem;

    :hover {
        text-decoration: none;
        background-color: ${({ theme }) => theme.colors.secondary[800]};
        color: ${({ theme }) => theme.colors.white};
    }
`;

export const DropdownItem = styled.li`
    list-style-type: none;
    display: flex;

    a {
        padding: 0.62rem 0.75rem;
        border-radius: 0.5rem;
        height: 100%;
        width: 100%;

        :hover {
            background-color: ${({ theme }) => theme.colors.secondary[700]};
            color: ${({ theme }) => theme.colors.white};
        }
    }

    ${({ theme }) =>
        mq.to(screen.LG) &&
        css`
            //border: none;
            font-size: ${theme.typography.fontSize.sm};
        `}

    :last-child {
        border-radius: 0 0 0.25rem 0.25rem;
    }
`;

export const DropdownLink = styled.a`
    color: ${({ theme }) => theme.colors.black};
    font-size: 1rem;

    :hover {
        text-decoration: none;
    }
`;

export const FavoriteLink = styled.a<{ awaiting?: boolean }>`
    position: relative;
    margin: 0 1.5rem 0 0;
    height: 26px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    svg {
        path {
            fill: ${({ theme }) => theme.colors.secondary[700]};
        }
    }

    ${(props) =>
        props.awaiting &&
        css`
            span {
                animation: pulse 1s infinite ease-in-out alternate;
            }
            @keyframes pulse {
                from {
                    transform: scale(0.5);
                }
                to {
                    transform: scale(1.8);
                }
            }
        `}
`;

interface CollapserWithArrowProps {
    arrowColor?: React.CSSProperties["color"];
    arrowText?: string;
    filterTooltipName?: "Vermogen" | "Trekgewicht";
    showTooltip?: boolean;
    icon?: JSX.Element;
    isOpen?: boolean;
    label?: string;
    testId: string;
    vdpOption?: boolean;
    vdpSubOption?: boolean;
    customButtonStyles?: string;
    onToggle?: (openStatus: boolean) => void;
    children?: React.ReactNode; // currently you have to set this explicitly as a prop for React.memo components: https://github.com/jleider/DefinitelyTyped/issues/7
    renderSeoContent?: boolean;
}

export const NavStyledArrow = styled.span<
    Pick<CollapserWithArrowProps, "arrowColor" | "arrowText" | "isOpen" | "vdpOption">
>`
    display: flex;
    align-items: center;

    @media (${MEDIA.lg}) {
        right: 4px;
    }

    svg {
        width: 1rem;
        transition: transform 0.5s ease;
        ${({ isOpen }) => isOpen && `transform: rotate(180deg);`};
        ${({ theme, isOpen }) => isOpen && `fill: ${theme.colors.white};`}
    }
`;

export const MobileMenu = styled.div<{ open: boolean }>`
    position: absolute;
    padding: 0 1.0625rem;
    bottom: 0;
    left: 0;
    right: 0;
    height: 95dvh;
    top: 52px;
    background-color: ${({ theme }) => theme.colors.secondary[50]};
    justify-content: space-between;
    flex-direction: column;
    display: none;

    ${({ open }) =>
        open &&
        css`
            display: flex;
        `};

    @media (${MEDIA.lg}) {
        display: none;
    }
`;

export const MobileMenuContainer = styled.div`
    width: 100%;
`;

export const MobileMenuMainWrapper = styled.div`
    margin: 2rem 0;
`;

export const MobileDropdownMenuHeader = styled.div<{ active?: boolean }>`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    cursor: pointer;
    width: 100%;
    padding: ${({ active }) => (active ? "0.625rem;" : "0 0.625rem;")};
    align-items: flex-start;
    gap: 1.25rem;
    align-self: stretch;
    border-radius: 0.25rem;
    color: ${({ active, theme }) => (active ? theme.colors.white : theme.colors.secondary[700])};
    background-color: ${({ active, theme }) =>
        active ? theme.colors.secondary[700] : theme.colors.secondary[50]};
    font-weight: 600;

    svg {
        fill: ${({ theme, active }) => (active ? theme.colors.white : theme.colors.secondary[700])};
    }
`;

export const MobileDropdownMenu = styled.ul<{ active?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 0.625rem;
    align-self: stretch;
    font-size: 0.875rem;
    font-weight: 400;
    font-style: normal;
    line-height: 1rem;
    color: ${({ theme }) => theme.colors.black};
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s linear, visibility 0.3s linear, max-height 0.3s ease-out;
    margin-left: -2.5rem;

    ${({ active }) =>
        active &&
        css`
            opacity: 1;
            visibility: visible;
            max-height: 200px;
        `}
`;

export const MobileDropdownItem = styled.li`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 0.625rem;
    gap: 0.625rem;
    align-self: stretch;

    a {
        color: ${({ theme }) => theme.colors.black};
        font-size: 0.875rem;
        font-weight: 400;
    }
`;

export const MobileAccountsBtnWrapper = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    padding: 1.8rem 0;
    gap: 1.8rem;
`;

export const MobileAuthButton = styled.a`
    all: unset;
    display: flex;
    height: 44px;
    padding: 0.65rem 0.75rem;
    justify-content: center;
    align-items: center;
    gap: 0.625rem;
    flex: 1 0 0;
    width: 100%;
    border-radius: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.secondary[700]};
    color: ${({ theme }) => theme.colors.secondary[700]};
    cursor: pointer;
    font-weight: 600;
    font-size: 0.87rem;
`;

export const MobileHRLine = styled.div`
    border: 0;
    height: 0.0625rem;
    background-color: ${({ theme }) => theme.colors.secondary[700]};
`;

export const DesktopHRLine = styled.div`
    border: 0;
    height: 0.0625rem;
    background-color: ${({ theme }) => theme.colors.secondary[700]};
    margin: 0.75rem 0;
`;
